/**
 * Positions — migrated from mockData to live svdy-platform API.
 *
 * Follows the Departments pattern (f9b4e04): drop local types + mockData,
 * use `Schema<'PositionResponse'>` as the source of truth, fetch on mount,
 * map UI → API in CRUD handlers.
 *
 * Wave 3.1 UX polish:
 *   - `department_id` dropdown is populated from the live departments list.
 *   - `level` is a Select 1-5 (number input was too permissive).
 *   - Status is a simple active/inactive toggle on the form.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useTranslation } from 'react-i18next';

import { CodeCell } from '../../../components/organization/CodeCell';
import { CodeField, isCodeValid } from '../../../components/organization/CodeField';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import { ImportDialog } from '../../../components/import/ImportDialog';
import { positionImportApi } from '../../../api/organization';
import type { ImportBatchSummary } from '../../../api/organization';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';

type Position = Schema<'PositionResponse'>;
type PositionCreate = Schema<'PositionCreate'>;
type PositionUpdate = Schema<'PositionUpdate'>;
type Department = Schema<'DepartmentResponse'>;

type FormState = Partial<Position> & { _isActive?: boolean };

const ACTIVE_STATUS = 'active';
const INACTIVE_STATUS = 'inactive';

const LEVELS = [1, 2, 3, 4, 5];

function isActive(p: Position): boolean {
  return (p.status ?? ACTIVE_STATUS) === ACTIVE_STATUS;
}

const Positions: React.FC = () => {
  const { t } = useTranslation();
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [positions, setPositions] = useState<Position[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<FormState | null>(null);
  const [positionToDelete, setPositionToDelete] = useState<Position | null>(
    null,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [importOpen, setImportOpen] = useState<boolean>(false);
  const [lastImport, setLastImport] = useState<ImportBatchSummary | null>(null);

  // -------------------------------------------------------------------------
  // Fetch — positions + departments in parallel
  // -------------------------------------------------------------------------

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pos, deps] = await Promise.all([
        organizationApi.positionsApi.list(),
        organizationApi.departmentsApi.list(),
      ]);
      setPositions(pos);
      setDepartments(deps);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:positions.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * The last-import line of §11.4. Its OWN fetch rather than part of `fetchAll`,
   * because a failure here must not blank the positions table — the history is
   * context, not the page's subject.
   */
  const fetchLastImport = useCallback(async () => {
    try {
      const batches = await positionImportApi.batches(1);
      setLastImport(batches[0] ?? null);
    } catch {
      setLastImport(null);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetchLastImport();
  }, [fetchLastImport]);

  const departmentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departments) map.set(d.id, d.name);
    return map;
  }, [departments]);

  // -------------------------------------------------------------------------
  // Summary KPIs
  // -------------------------------------------------------------------------

  const summaryStats = useMemo(() => {
    const total = positions.length;
    const staffed = positions.filter((p) => (p.worker_count ?? 0) > 0).length;
    const vacant = positions.filter(
      (p) => (p.worker_count ?? 0) === 0 && isActive(p),
    ).length;
    const inactive = positions.filter((p) => !isActive(p)).length;
    return { total, staffed, vacant, inactive };
  }, [positions]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:positions.stats.total'),
        description: t('org:positions.stats.totalDescription'),
        avatar: {
          icon: <WorkOutlineIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'staffed',
        value: summaryStats.staffed,
        title: t('org:positions.stats.staffed'),
        description: t('org:positions.stats.staffedDescription'),
        avatar: {
          icon: <PeopleAltOutlinedIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'vacant',
        value: summaryStats.vacant,
        title: t('org:positions.stats.vacant'),
        description: t('org:positions.stats.vacantDescription'),
        avatar: {
          icon: <ErrorOutlineIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
      {
        id: 'inactive',
        value: summaryStats.inactive,
        title: t('org:positions.stats.inactive'),
        description: t('org:positions.stats.inactiveDescription'),
        avatar: {
          icon: <BlockOutlinedIcon />,
          bgcolor: 'grey.200',
          color: 'text.secondary',
        },
      },
    ],
    [summaryStats, t],
  );

  // -------------------------------------------------------------------------
  // Filtering + pagination
  // -------------------------------------------------------------------------

  const filtered = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return positions;
    return positions.filter(
      (p) =>
        p.title.toLowerCase().includes(needle) ||
        (p.code ?? '').toLowerCase().includes(needle) ||
        (p.description ?? '').toLowerCase().includes(needle) ||
        (p.department_name ?? '').toLowerCase().includes(needle),
    );
  }, [positions, filterText]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // -------------------------------------------------------------------------
  // Form handlers
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!currentPosition) return false;
    const errs: Record<string, string> = {};
    if (!currentPosition.title?.trim()) {
      errs.title = t('org:positions.fields.titleRequired');
    }
    // ADR-043 D6. The field shows the specific rule under itself; this stops the
    // submit. The server refuses it too, but making the admin wait for a round
    // trip to learn about a space in a code is a worse experience for the same
    // outcome.
    if (!isCodeValid(currentPosition?.code || '')) {
      errs.code = t('org:shared.codeField.label');
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    setCurrentPosition({ _isActive: true });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (pos: Position) => {
    setCurrentPosition({ ...pos, _isActive: isActive(pos) });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrentPosition(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    if (!validateForm() || !currentPosition) return;
    setSaving(true);
    try {
      if (currentPosition.id) {
        const update: PositionUpdate = {
          title: currentPosition.title ?? null,
          code: currentPosition.code ?? null,
          description: currentPosition.description ?? null,
          department_id: currentPosition.department_id ?? null,
          level: currentPosition.level ?? null,
          status: currentPosition._isActive ? ACTIVE_STATUS : INACTIVE_STATUS,
        };
        await organizationApi.positionsApi.update(currentPosition.id, update);
        setToast(t('org:positions.toast.updated', { name: currentPosition.title }));
      } else {
        const create: PositionCreate = {
          title: currentPosition.title!,
          code: currentPosition.code ?? null,
          description: currentPosition.description ?? null,
          department_id: currentPosition.department_id ?? null,
          level: currentPosition.level ?? null,
        };
        await organizationApi.positionsApi.create(create);
        setToast(t('org:positions.toast.created', { name: create.title }));
      }
      handleCloseFormDialog();
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:positions.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (pos: Position) => {
    setPositionToDelete(pos);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setPositionToDelete(null);
  };

  const handleDelete = async () => {
    if (!positionToDelete) return;
    setSaving(true);
    try {
      await organizationApi.positionsApi.delete(positionToDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== positionToDelete.id));
      // Dialog auto-closes via its onClose on successful confirm; we
      // still clear the row selection here.
      handleCloseDeleteDialog();
      setToast(t('org:positions.toast.deleted', { name: positionToDelete.title }));
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:positions.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Export — the IMPORT TEMPLATE, deliberately, not a snapshot of the table.
  //
  // This page had NO export at all until 2026-08-09, which is what the owner
  // reported. The reason to make it the template rather than a client-side CSV is the
  // trap import slice 3.1 already paid for on the workers page: a display snapshot
  // writes `Department` as a NAME, so an admin who edits it and re-uploads has every
  // FK column fail — a file that almost round-trips is worse than one that obviously
  // does not. One producer, `department_code` beside the name, a BOM so Excel opens
  // CJK titles correctly, and the current filter carried as ids.
  const handleExport = async () => {
    try {
      await positionImportApi.downloadTemplate(false, filtered.map((x) => x.id));
      setToast(t('org:positions.toast.exportDone', { count: filtered.length }));
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.errorGeneric'),
      );
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:positions.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.positionsApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:positions.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:positions.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setCurrentPosition((prev) =>
      prev
        ? {
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
          }
        : null,
    );
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrentPosition((prev) =>
      prev
        ? {
            ...prev,
            [name]: value === '' ? null : value,
          }
        : null,
    );
  };

  // -------------------------------------------------------------------------
  // Row selection / pagination
  // -------------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((p) => p.id) : []);
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------

  if (loading && positions.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && positions.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      {/* Title + actions */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:positions.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:positions.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:positions.addButton')}
          </Button>
          {/* Outlined, so the toolbar keeps exactly ONE contained button. */}
          <Button
            variant="outlined"
            onClick={() => setImportOpen(true)}
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.2)',
              bgcolor: '#ffffff',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.35)',
                bgcolor: '#ffffff',
              },
            }}
          >
            {t('org:import.entities.positions.title')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleExport}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.2)',
              bgcolor: '#ffffff',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.35)',
                bgcolor: '#ffffff',
              },
            }}
          >
            {t('common:export.button')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>

      {/*
        §11.4 — "Last import: 12 positions, 2026-08-08 14:12, by Robin".
        A line, not a route: this is also how an admin answers "who changed this",
        and it belongs where the data is.
      */}
      {lastImport && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            lastImport.created_by_name
              ? 'org:import.entities.positions.historyLast'
              : 'org:import.entities.positions.historyLastNoUser',
            {
              count: lastImport.row_count,
              when: fmtLocalDateTime(lastImport.created_at),
              who: lastImport.created_by_name,
            },
          )}
          {lastImport.undone_at && (
            <Typography component="span" variant="body2" color="text.disabled">
              {' · '}
              {t('org:import.history.undoneAt', {
                when: fmtLocalDateTime(lastImport.undone_at),
              })}
            </Typography>
          )}
        </Typography>
      )}
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      {/* Filter + pagination */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          variant="outlined"
          label={t('org:positions.filterLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: '400px', maxWidth: '100%', mr: 2 }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText
              ? t('org:positions.empty.noMatches')
              : t('org:positions.empty.noResults')}
          </Typography>
          {!filterText && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:positions.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:positions.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:positions.table.title')}</TableCell>
                <TableCell>{t('org:positions.table.code')}</TableCell>
                <TableCell>{t('org:positions.table.department')}</TableCell>
                <TableCell>{t('org:positions.table.level')}</TableCell>
                <TableCell>{t('org:positions.table.workers')}</TableCell>
                <TableCell>{t('org:positions.table.status')}</TableCell>
                <TableCell align="center">{t('org:positions.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((pos) => {
                const selected = isSelected(pos.id);
                const depName =
                  pos.department_name ??
                  (pos.department_id
                    ? departmentNameById.get(pos.department_id) ?? '-'
                    : '-');
                return (
                  <TableRow
                    hover
                    key={pos.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, pos.id)}
                        inputProps={{ 'aria-label': pos.title }}
                      />
                    </TableCell>
                    <TableCell>{pos.title}</TableCell>
                    <TableCell>
                      <CodeCell code={pos.code} codeSource={pos.code_source} />
                    </TableCell>
                    <TableCell>{depName}</TableCell>
                    <TableCell>{pos.level ?? '-'}</TableCell>
                    <TableCell>{pos.worker_count ?? 0}</TableCell>
                    <TableCell>{pos.status ?? ACTIVE_STATUS}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label={t('org:shared.table.edit')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(pos);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('common:actions.delete')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(pos);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, py: 2 }}>
          {currentPosition?.id ? t('org:positions.editDialogTitle') : t('org:positions.addDialogTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label={t('org:positions.fields.title')}
            fullWidth
            variant="outlined"
            value={currentPosition?.title || ''}
            onChange={handleFormChange}
            error={!!formErrors.title}
            helperText={formErrors.title}
            sx={{ mb: 2 }}
          />
          <CodeField
            value={currentPosition?.code || ''}
            onChange={(code) =>
              setCurrentPosition((prev) => (prev ? { ...prev, code } : prev))
            }
            codeSource={currentPosition?.code_source}
          />
          <TextField
            margin="dense"
            name="description"
            label={t('org:positions.fields.description')}
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={currentPosition?.description || ''}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="department-label">{t('org:positions.fields.department')}</InputLabel>
            <Select
              labelId="department-label"
              name="department_id"
              value={currentPosition?.department_id || ''}
              label={t('org:positions.fields.department')}
              onChange={handleSelectChange}
            >
              <MenuItem value="">
                <em>{t('org:shared.form.none')}</em>
              </MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="level-label">{t('org:positions.fields.level')}</InputLabel>
            <Select
              labelId="level-label"
              name="level"
              value={currentPosition?.level ?? ''}
              label={t('org:positions.fields.level')}
              onChange={handleSelectChange}
            >
              <MenuItem value="">
                <em>{t('org:shared.form.none')}</em>
              </MenuItem>
              {LEVELS.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {t('org:positions.fields.levelOption', { n: lvl })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentPosition?._isActive ?? true}
                onChange={handleFormChange}
                name="_isActive"
                color="primary"
              />
            }
            label={t('org:positions.fields.active')}
          />
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : currentPosition?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:positions.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:positions.deleteTitle')}
        description={t('org:positions.deleteDescription')}
        subject={
          positionToDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {positionToDelete.title}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      {/* `entity="positions"` — ONE dialog for every sheet. A copy would have to
          re-establish each of the four steps' guarantees (the token is over the
          PLAN, Apply is ABSENT rather than disabled when the report has findings)
          and one copy would drift. */}
      <ImportDialog
        open={importOpen}
        entity="positions"
        onClose={() => setImportOpen(false)}
        onApplied={() => {
          fetchAll();
          fetchLastImport();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Positions;
